import { ApiFile } from "@/types/common.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { ApiFilterBase } from "@skailer-com/uikit/core";

export enum ApiIntegrationType {
  gmail = "gmail",
  outlook = "outlook",
  google_calendar_service_account = "google-calendar-service-account",
  google_workspace = "google-workspace",
  slack = "slack",
  smtp = "smtp",
  zoom = "zoom",
}

export interface ApiIntegration {
  id: number;
  is_active: boolean;
  identity: string;
  integration_type: ApiIntegrationType;
  datetime_last_check: Date;
  credentials?: string;
}

export interface ApiIntegrationLoginUrl {
  authorization_url: string;
}

export interface ApiEmailBaseSendPayload {
  from_email: string;
  subject: string;
  body: string;
  cc_email?: string;
  bcc_email?: string;
  file_uuids?: string[];
  thread_id?: string;
  follow_ups: ApiEmailFollowUpPayload[];
}

export interface ApiEmailFollowUpPayload {
  delay_days: number;
  body: string;
}

export enum ApiEmailSequenceRunStatus {
  active = "active",
  completed = "completed",
  replied = "replied",
  canceled = "canceled",
  failed = "failed",
}

export interface ApiEmailSequenceRunSummary {
  id: number;
  status: ApiEmailSequenceRunStatus;
  total_follow_ups: number;
  sent_follow_ups: number;
  datetime_next_send_at_from: string | null;
  datetime_next_send_at_to: string | null;
  datetime_stopped_at: string | null;
}

export enum ApiEmailStatus {
  pending = "pending",
  queued = "queued",
  sent = "sent",
  canceled = "canceled",
  failed = "failed",
  received = "received",
}

export interface ApiEmailSendPayload extends ApiEmailBaseSendPayload {
  candidate_id: number;
  to_email: string;
}

export interface ApiEmail {
  id: number;
  // owner_id: number;
  candidate_id: number;
  from_email: string;
  to_email: string;
  subject: string;
  body: string;
  is_outgoing: boolean;
  cc_email: string;
  bcc_email: string;
  original_thread_id: string;
  files: ApiFile[];
  is_unread: boolean;
  status: ApiEmailStatus;
  sequence_position: number | null;
  sequence_total: number | null;
  sequence_run?: ApiEmailSequenceRunSummary | null;
}

export interface ApiIntegrationGoogleCalendarServiceAccountSave {
  is_active: boolean;
  credentials?: string | null;
  identity?: string | null;
  sync_out_of_office_events?: boolean;
}

export interface ApiIntegrationGoogleCalendarServiceAccount {
  id: number;
  is_active: boolean;
  identity: string;
  integration_type: ApiIntegrationType;
  credentials?: string;
  sync_out_of_office_events: boolean;
  not_found_employees_count: number;
}

export interface ApiIntegrationSlack {
  id: number;
  is_active: boolean;
  has_credentials: boolean;
  identity: string;
  integration_type: ApiIntegrationType;

  sync_photo: boolean;
  sync_name: boolean;
  sync_hired_on: boolean;
  sync_address: boolean;
  sync_position: boolean;
  sync_position_division: boolean;

  is_all_scopes_installed: boolean;
  not_found_employees_count: number;
}

export enum ApiIntegrationEmployeeLinkStatus {
  linked = "linked",
  not_found = "not_found",
}

export interface ApiIntegrationEmployeesFilter extends ApiFilterBase {
  status?: ApiIntegrationEmployeeLinkStatus;
  text?: string;
}

export interface ApiSlackEmployee {
  employee: ApiEmployeeShort;
  email: string | null;
  status: ApiIntegrationEmployeeLinkStatus;
  slack_user_id: string | null;
  slack_email: string | null;
  slack_name: string | null;
}

export interface ApiGoogleCalendarEmployee {
  employee: ApiEmployeeShort;
  email: string | null;
  status: ApiIntegrationEmployeeLinkStatus;
  calendar_email: string | null;
}

export interface ApiIntegrationEmployeeByType {
  [ApiIntegrationType.slack]: ApiSlackEmployee;
  [ApiIntegrationType.google_calendar_service_account]: ApiGoogleCalendarEmployee;
}

export type ApiIntegrationEmployeesType = keyof ApiIntegrationEmployeeByType;

export interface ApiIntegrationSlackSave {
  is_active: boolean;
  sync_photo: boolean;
  sync_name: boolean;
  sync_hired_on: boolean;
  sync_address: boolean;
  sync_position: boolean;
  sync_position_division: boolean;
}
export interface ApiSlackChannel {
  channel_id: string;
  name: string;
  num_members: number;
  purpose: string;
  is_private: boolean;
  is_archived: boolean;
}

export interface ApiIntegrationEmailServers {
  id: number;
  email: string;
  smtp_server: string;
  smtp_port: number;
  smtp_use_tls: boolean;
  imap_or_pop3_server: string;
  imap_or_pop3_port: number;
  imap_or_pop3_use_ssl: boolean;
  protocol: string;
  delete_after_fetch: boolean;
  fetch_limit: number;
  is_active: boolean;
  datetime_last_check: string;
}

export interface ApiIntegrationEmailServersSave {
  email: string;
  password: string;
  smtp_server: string;
  smtp_port: number;
  smtp_use_tls: boolean;
  imap_or_pop3_server: string;
  imap_or_pop3_port: number;
  imap_or_pop3_use_ssl: boolean;
  protocol: string;
}

export enum ApiEmailServerProtocol {
  pop3 = "pop3",
  imap = "imap",
}

export const ApiEmailServerProtocolItems = [
  {
    id: ApiEmailServerProtocol.pop3,
    title: "POP3",
  },
  {
    id: ApiEmailServerProtocol.imap,
    title: "IMAP",
  },
] as SelectItem[];

export interface ApiIntegrationGoogleWorkspace {
  id: number;
  is_active: boolean;
  has_credentials: boolean;
  identity: string | null;
  integration_type: ApiIntegrationType;

  // sync_photo: boolean;
  // sync_name: boolean;
  // sync_hired_on: boolean;
  // sync_address: boolean;
  // sync_position: boolean;
  // sync_position_division: boolean;
  //
  // is_all_scopes_installed: boolean;
}

export interface ApiIntegrationGoogleWorkspaceSave {
  is_active: boolean;
  // sync_photo: boolean;
  // sync_name: boolean;
  // sync_hired_on: boolean;
  // sync_address: boolean;
  // sync_position: boolean;
  // sync_position_division: boolean;
}
